"use client"

import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { format } from 'date-fns';
import { CalendarIcon, Loader2 } from 'lucide-react';
import DealService from '@/api/deal.service';
import CustomerService from '@/api/customers.service';
import CompanyService from '@/api/companies.service';
import { cn } from '@/lib/utils';

const formSchema = z.object({
  title: z.string().min(1, 'Deal title is required'),
  dealPipelineId: z.string().min(1, 'Pipeline is required'),
  customerId: z.string().optional(),
  companyId: z.string().optional(),
  dealStageId: z.string().min(1, 'Stage is required'),
  value: z.string().min(1, 'Deal value is required').refine(
    (val) => !isNaN(Number(val)) && Number(val) > 0,
    { message: 'Deal value must be a positive number' }
  ),
  currency: z.string().min(1, 'Currency is required'),
  probability: z.string().min(1, 'Probability is required').refine(
    (val) => !isNaN(Number(val)) && Number(val) >= 0 && Number(val) <= 100,
    { message: 'Probability must be between 0 and 100' }
  ),
  expectedCloseDate: z.date({
    required_error: "Expected close date is required",
  }),
  description: z.string().optional(),
}).refine((data) => data.customerId || data.companyId, {
  message: "Either customer or company must be selected",
  path: ["customerId"],
});

interface CreateDealFormProps {
  businessId: number;
  pipelineStages: any[];
  onSuccess: () => void;
}

export default function CreateDealForm({ businessId, pipelineStages, onSuccess }: CreateDealFormProps) {
  const [customers, setCustomers] = useState<any[]>([]);
  const [companies, setCompanies] = useState<any[]>([]);
  const [pipeline, setPipeline] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetchingData, setIsFetchingData] = useState(true);
  const { toast } = useToast();
  
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      title: '',
      dealPipelineId: '',
      customerId: '',
      companyId: '',
      dealStageId: pipelineStages[0]?.id.toString() || '',
      value: '',
      currency: 'USD',
      probability: '',
      description: '',
    },
  });
  
  useEffect(() => {
    const fetchData = async () => {
      setIsFetchingData(true);
      try {
        // Fetch pipeline data
        const pipelineResponse = await DealService.getPipeline(businessId);
        if (pipelineResponse.data.result === 'success') {
          setPipeline(pipelineResponse.data.dealPipeline);
          form.setValue('dealPipelineId', pipelineResponse.data.dealPipeline.id.toString());
        }

        // Fetch customers
        const customersResponse = await CustomerService.getAllCustomers(businessId);
        if (customersResponse.data.result === 'success') {
          setCustomers(customersResponse.data.customers || []);
        }

        // Fetch companies
        const companiesResponse = await CompanyService.getAllCompanies(businessId);
        if (companiesResponse.data.result === 'success') {
          setCompanies(companiesResponse.data.companies || []);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        toast({
          title: "Error",
          description: "Failed to load form data.",
          variant: "destructive",
        });
      } finally {
        setIsFetchingData(false);
      }
    };
    
    fetchData();
  }, [businessId, toast, form]);

  // Update probability when stage changes
  const watchStageId = form.watch('dealStageId');
  useEffect(() => {
    if (watchStageId && pipelineStages.length > 0) {
      const selectedStage = pipelineStages.find(stage => stage.id.toString() === watchStageId);
      if (selectedStage) {
        console.log("selectedStage", selectedStage);
        form.setValue('probability', selectedStage.defaultProbability.toString());
      }
    }
  }, [watchStageId, pipelineStages, form]);
  
  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    setIsLoading(true);
    try {
      const dealData = {
        title: values.title,
        dealPipelineId: parseInt(values.dealPipelineId),
        customerId: values.customerId ? parseInt(values.customerId) : null,
        companyId: values.companyId ? parseInt(values.companyId) : null,
        dealStageId: parseInt(values.dealStageId),
        value: parseFloat(values.value),
        currency: values.currency,
        probability: parseInt(values.probability),
        expectedCloseDate: values.expectedCloseDate,
        description: values.description || '',
      };
      
      const response = await DealService.createDeal(businessId, dealData);
      
      if (response.data.result === 'success') {
        toast({
          title: "Success",
          description: "Deal created successfully!",
        });
        onSuccess();
      } else {
        toast({
          title: "Error",
          description: response.data.message || "Failed to create deal.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error creating deal:", error);
      toast({
        title: "Error",
        description: "An error occurred while creating the deal.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  if (isFetchingData) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin mr-2" />
        Loading form data...
      </div>
    );
  }
  
  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <FormField
          control={form.control}
          name="title"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Deal Title</FormLabel>
              <FormControl>
                <Input placeholder="New Product Purchase" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="dealPipelineId"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Pipeline</FormLabel>
              <Select onValueChange={field.onChange} value={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select pipeline" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {pipeline && (
                    <SelectItem value={pipeline.id.toString()}>
                      {pipeline.name}
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="customerId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Customer (Optional)</FormLabel>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a customer" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {customers.map((customer) => (
                      <SelectItem key={customer.id} value={customer.id.toString()}>
                        {customer.firstName} {customer.lastName}
                        {customer.companyName && ` (${customer.companyName})`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="companyId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Company (Optional)</FormLabel>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a company" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {companies.map((company) => (
                      <SelectItem key={company.id} value={company.id.toString()}>
                        {company.companyName || company.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <FormField
            control={form.control}
            name="value"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Deal Value</FormLabel>
                <FormControl>
                  <Input type="number" min="0" placeholder="5000" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="currency"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Currency</FormLabel>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select currency" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="USD">USD</SelectItem>
                    <SelectItem value="EUR">EUR</SelectItem>
                    <SelectItem value="GBP">GBP</SelectItem>
                    <SelectItem value="ZWG">ZWG</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="dealStageId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Pipeline Stage</FormLabel>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select stage" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {pipelineStages.map((stage) => (
                      <SelectItem key={stage.id} value={stage.id.toString()}>
                        {stage.name} ({stage.defaultProbability}%)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="probability"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Probability (%)</FormLabel>
              <FormControl>
                <Input 
                  type="number" 
                  min="0" 
                  max="100" 
                  placeholder="50"
                  {...field} 
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="expectedCloseDate"
          render={({ field }) => (
            <FormItem className="flex flex-col">
              <FormLabel>Expected Close Date</FormLabel>
              <Popover>
                <PopoverTrigger asChild>
                  <FormControl>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full pl-3 text-left font-normal",
                        !field.value && "text-muted-foreground"
                      )}
                    >
                      {field.value ? (
                        format(field.value, "PPP")
                      ) : (
                        <span>Pick a date</span>
                      )}
                      <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                    </Button>
                  </FormControl>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={field.value}
                    onSelect={field.onChange}
                    disabled={(date) => date < new Date()}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Description</FormLabel>
              <FormControl>
                <Textarea 
                  placeholder="Details about the deal..."
                  className="resize-none"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <div className="flex justify-end gap-3">
          <Button type="button" variant="outline" onClick={onSuccess}>
            Cancel
          </Button>
          <Button type="submit" disabled={isLoading}>
            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Create Deal
          </Button>
        </div>
      </form>
    </Form>
  );
} 